import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { paddedPercent } from '~/domain/services/imageService';
import { formatMmAsCm } from '~/utils/units';
import InfoHint from '~/components/Inputs/InfoHint';

interface ReformatWarningsProps {
    /** Proportion of the output that will be padding, 0..1. */
    paddedFraction: number;
    replicatedSlices: number;
    extensionMarginMm: number;
    /** Acquired image the extension will leave out, mm. */
    excludedMm: number;
    /**
     * Extent of the source filled by duplicating a neighbouring slice, mm.
     *
     * The third way this tool produces content the patient was not scanned
     * for, and the one the operator was not being told about: it was
     * recorded in the derived series while the person accepting the
     * operation saw nothing.
     */
    gapFilledMm: number;
}

/**
 * What the output will contain that the patient was not scanned for.
 *
 * Shown beside the control that starts the reformat rather than at the
 * foot of the parameter column, because that column scrolls: at a laptop
 * height the geometry block sits below the fold while Start stays pinned,
 * so the decision could be taken without the statement ever being on
 * screen.
 */
export default function ReformatWarnings({
    paddedFraction,
    replicatedSlices,
    extensionMarginMm,
    excludedMm,
    gapFilledMm,
}: ReformatWarningsProps) {
    const intl = useIntl();
    const percent = paddedPercent(paddedFraction);

    if (
        percent === 0 &&
        replicatedSlices === 0 &&
        excludedMm === 0 &&
        gapFilledMm === 0
    ) {
        return null;
    }

    return (
        <Box flex={1} minWidth={0} textAlign="left">
            {percent > 0 && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                >
                    {intl.formatMessage(
                        { id: 'reformatPaddedWarning' },
                        { percent }
                    )}
                    <InfoHint
                        title={intl.formatMessage({ id: 'reformatPaddedInfo' })}
                        placement="left"
                    />
                </Typography>
            )}
            {replicatedSlices > 0 && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                >
                    {intl.formatMessage(
                        { id: 'reformatReplicatedWarning' },
                        { count: replicatedSlices }
                    )}
                    <InfoHint
                        title={intl.formatMessage(
                            { id: 'reformatReplicatedInfo' },
                            { margin: formatMmAsCm(extensionMarginMm, 1) }
                        )}
                        placement="left"
                    />
                </Typography>
            )}
            {gapFilledMm > 0 && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                >
                    {intl.formatMessage(
                        { id: 'reformatGapFilledWarning' },
                        { extent: formatMmAsCm(gapFilledMm, 1) }
                    )}
                    <InfoHint
                        title={intl.formatMessage({
                            id: 'reformatGapFilledInfo',
                        })}
                        placement="left"
                    />
                </Typography>
            )}
            {excludedMm > 0 && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                >
                    {intl.formatMessage(
                        { id: 'reformatExcludedWarning' },
                        { extent: formatMmAsCm(excludedMm, 1) }
                    )}
                    <InfoHint
                        title={intl.formatMessage({
                            id: 'reformatExcludedInfo',
                        })}
                        placement="left"
                    />
                </Typography>
            )}
        </Box>
    );
}
